import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Button } from "@jti/core-components/components/Button";

import type { Language, PopupContext } from "../../LanguageSelector";
import { LanguageSelectorList } from "../LanguageSelectorList";

import styles from "./LanguageSelectorPopupMobile.module.scss";

type LanguageSelectorPopupMobileProps = {
  languages: Language[];
  onApplyLanguage: (language: Language) => void;
  onClose: () => void;
  isOpened: boolean;
  popupContext: PopupContext;
};

export const LanguageSelectorPopupMobile: FC<LanguageSelectorPopupMobileProps> = ({
  languages,
  onClose,
  onApplyLanguage,
  isOpened,
  popupContext,
}) => {
  const { __ } = useTranslations();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>();

  const currentPageLanguage = useMemo(() => languages.find(({ current }) => current), [languages]);
  const isApplyButtonDisabled = selectedLanguage?.languageDisplayName === currentPageLanguage?.languageDisplayName;

  useEffect(() => {
    setSelectedLanguage(currentPageLanguage);
  }, [currentPageLanguage]);

  const applyLanguage = () => {
    if (selectedLanguage) {
      onApplyLanguage(selectedLanguage);
      onClose();
    }
  };

  if (!isOpened) {
    return null;
  }

  return createPortal(
    <div
      data-testid="languageSelectorPopupMobile"
      className={classNames(styles.modal, {
        [styles.modalAgeConfirmation]: popupContext === "AGE_CONFIRMATION",
        [styles.modalMobileNavigation]: popupContext === "NAVIGATION",
        "light-theme": popupContext === "AGE_CONFIRMATION",
      })}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <button data-testid="languageSelectorCloseBtn" onClick={onClose}>
            <UiCrossIcon className={styles.closeButton} width="24px" height="24px" />
          </button>
        </div>
        <div className={styles.contentWrapper}>
          <h3 className={styles.mainText}>{__("languageselector.select_language.headline")}</h3>
          <LanguageSelectorList
            languages={languages}
            selectedLanguage={selectedLanguage || currentPageLanguage}
            onSelectLanguage={setSelectedLanguage}
          />
          <div className={styles.buttonsWrapper}>
            <Button
              onClick={applyLanguage}
              className={styles.button}
              data-testid="languageSelectorApplyBtn"
              variant="primary"
              size="sm"
              disabled={isApplyButtonDisabled}
            >
              {__("languageselector.apply.btn")}
            </Button>
            <Button
              onClick={onClose}
              variant="secondary"
              size="sm"
              className={styles.button}
              data-testid="languageSelectorCancelBtn"
            >
              {__("languageselector.cancel.btn")}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
